import React from 'react'
import { ICellEditorParams, ICellRendererParams } from 'ag-grid-community';
import { Hours, ImportRowParsed } from './types';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover/popover';
import { i18n } from "../../lib/i18n";

type TData = ImportRowParsed['hours']

const Days = [i18n.t('resource_import.sunday'), i18n.t('resource_import.monday'), i18n.t('resource_import.tuesday'), i18n.t('resource_import.wednesday'), i18n.t('resource_import.thursday'), i18n.t('resource_import.friday'), i18n.t('resource_import.saturday')]

type HoursRendererProps = Pick<ICellRendererParams<TData>, 'value'>

export function HoursRenderer(props: HoursRendererProps) {
  return (
    <div className='resource-import-edit-grid__hours'>
      {Days.map((day, idx) => {
        const isOpen = !!(props.value && props.value[idx])
        
        const className = [
          'resource-import-edit-grid__hours-day',
          isOpen ?
            'resource-import-edit-grid__hours-day--open text-green-600' :
            'resource-import-edit-grid__hours-day--closed text-gray-600',

        ].join(' ')
        
        return <React.Fragment key={day}>
          <span className={className}>{day[0]}</span>&nbsp;
        </React.Fragment>
      })}
    </div>
  )
}

interface HoursEditorProps extends ICellEditorParams<TData> {
  value: Hours[] | null | undefined,
  onValueChange(value: Hours[]): void;
  stopEditing(): void;
}

export function HoursEditor(props: HoursEditorProps) {
  
  const [hours, setHours] = React.useState(props.value || [])
  
  return (
    <div className='resource-import-edit-grid__hours'>
      <Popover defaultOpen onOpenChange={()=> {
        props.onValueChange(hours)
        props.stopEditing()
      }}>
        <PopoverTrigger>
          <HoursRenderer value={hours} />
        </PopoverTrigger>
        <PopoverContent className="bg-white bg-scroll w-[calc(100vw-2rem)] max-h-[80vh] overflow-y-auto sm:w-[400px] lg:w-[600px] max-w-none" side="bottom" align="start" sideOffset={5}>
          <div className='resource-import-edit-grid__hours-editor'>
            <div className="flex flex-row text-gray-500 text-xs uppercase pb-1 font-medium">
              <div className="basis-3/12">
                <span>{i18n.t('resource_import.day')}</span>
              </div>
              <div className="basis-2/12">
                <span>{i18n.t('resource_import.active_question')}</span>
              </div>
              <div className="basis-7/12 flex items-center justify-center">
                <span>{i18n.t('resource_import.hours_label')}</span>
              </div>
            </div>

            <form className="flex flex-col gap-2">
              {Days.map((day, idx) => {
                return <HoursDayInputs
                  key={day}
                  value={hours[idx]}
                  label={day}
                  onChange={(hrs) => {
                    let newHours = [...hours]
                    newHours[idx] = hrs
                    setHours(newHours)
                  }}
                />
              })}
            </form>
          </div>
        </PopoverContent>
      </Popover>
    </div>

  )
}

const TimeOptions = [
  [i18n.t('resource_import.open_all_day'), ''],
  ['1:00 AM', '0100'],
  ['1:30 AM', '0130'],
  ['2:00 AM', '0200'],
  ['2:30 AM', '0230'],
  ['3:00 AM', '0300'],
  ['3:30 AM', '0330'],
  ['4:00 AM', '0400'],
  ['4:30 AM', '0430'],
  ['5:00 AM', '0500'],
  ['5:30 AM', '0530'],
  ['6:00 AM', '0600'],
  ['6:30 AM', '0630'],
  ['7:00 AM', '0700'],
  ['7:30 AM', '0730'],
  ['8:00 AM', '0800'],
  ['8:30 AM', '0830'],
  ['9:00 AM', '0900'],
  ['9:30 AM', '0930'],
  ['10:00 AM', '1000'],
  ['10:30 AM', '1030'],
  ['11:00 AM', '1100'],
  ['11:30 AM', '1130'],
  ['12:00 PM', '1200'],
  ['12:30 PM', '1230'],
  ['1:00 PM', '1300'],
  ['1:30 PM', '1330'],
  ['2:00 PM', '1400'],
  ['2:30 PM', '1430'],
  ['3:00 PM', '1500'],
  ['3:30 PM', '1530'],
  ['4:00 PM', '1600'],
  ['4:30 PM', '1630'],
  ['5:00 PM', '1700'],
  ['5:30 PM', '1730'],
  ['6:00 PM', '1800'],
  ['6:30 PM', '1830'],
  ['7:00 PM', '1900'],
  ['7:30 PM', '1930'],
  ['8:00 PM', '2000'],
  ['8:30 PM', '2030'],
  ['9:00 PM', '2100'],
  ['9:30 PM', '2130'],
  ['10:00 PM', '2200'],
  ['10:30 PM', '2230'],
  ['11:00 PM', '2300'],
  ['11:30 PM', '2330'],
  ['12:00 AM', '2400']
] as const

function HoursDayInputs({ value, label, onChange }: { value: Hours | null | undefined, label: string, onChange(value: Hours): void }) {
  const [isOpen, setIsOpen] = React.useState(!!value)
  
  const open = value?.open || ''
  const close = value?.close || ''
  
  return (
    <div className="flex flex-row items-center">
      <div className="basis-3/12">
        <div className="text-gray-700 text-sm">{label}</div>
      </div>

      <div className="basis-2/12 flex">
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type='checkbox'
            checked={isOpen}
            onChange={() => {
              setIsOpen(!isOpen)
              onChange(isOpen ? null : {
                open,
                close,
              })
            }}
            className="toggle toggle-sm toggle-primary hover:toggle-primary focus:toggle-primary"
          />
        </label>
      </div>

      <div className="basis-7/12 flex items-center justify-center gap-2">
        <select
          value={open}
          disabled={!isOpen}
          className="select select-sm select-bordered rounded-md text-xs"
          onChange={(e) => {
            onChange({
              ...value,
              open: e.target.value,
            })
          }}
        >
          {TimeOptions.map(([label, value]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
        <span className="text-gray-500 text-xs">TO</span>
        <select
          value={close}
          disabled={!isOpen}
          className="select select-sm select-bordered rounded-md text-xs"
          onChange={(e) => {
            onChange({
              ...value,
              close: e.target.value,
            })
          }}
        >
          {TimeOptions.map(([label, value]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
      </div>
    </div>
  );
}
